import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Text,
  IconButton,
  DefaultButton,
  TooltipHost,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import { DataGrid, DataGridColumn } from "../../ui-controls/DataGrid";
import ShimmerList from "../../ui-controls/ShimmerList";
import {
  getCompanyNameAudits,
  ExpressCompanyNameAuditDto,
} from "../../api/renameCompany";

const formatDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : "—";

const RenameCompanyAuditsPage = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<ExpressCompanyNameAuditDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchAudits = useCallback(() => {
    setLoading(true);
    setErrorMessage("");
    getCompanyNameAudits()
      .then((data) => setAudits(data || []))
      .catch(() => setErrorMessage("Failed to load audits. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const columns: DataGridColumn[] = [
    {
      key: "dateRequested",
      name: "Date Requested",
      minWidth: 150,
      maxWidth: 190,
      isResizable: true,
      onRender: (item: ExpressCompanyNameAuditDto) => formatDate(item.dateRequested),
    },
    {
      key: "dateCompleted",
      name: "Date Completed",
      minWidth: 150,
      maxWidth: 190,
      isResizable: true,
      onRender: (item: ExpressCompanyNameAuditDto) => formatDate(item.dateCompleted),
    },
    {
      key: "serverId",
      name: "Server",
      fieldName: "serverId",
      minWidth: 80,
      maxWidth: 110,
      isResizable: true,
    },
    {
      key: "companyId",
      name: "Company ID",
      fieldName: "companyId",
      minWidth: 90,
      maxWidth: 110,
      isResizable: true,
    },
    {
      key: "databaseLetter",
      name: "Database",
      fieldName: "databaseLetter",
      minWidth: 70,
      maxWidth: 90,
      isResizable: true,
    },
    {
      key: "oldCompanyName",
      name: "Old Name",
      fieldName: "oldCompanyName",
      minWidth: 160,
      maxWidth: 240,
      isResizable: true,
    },
    {
      key: "newCompanyName",
      name: "New Name",
      fieldName: "newCompanyName",
      minWidth: 160,
      maxWidth: 240,
      isResizable: true,
    },
    {
      key: "status",
      name: "Status",
      fieldName: "status",
      minWidth: 110,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: "requestedBy",
      name: "Requested By",
      fieldName: "requestedBy",
      minWidth: 180,
      maxWidth: 280,
      isResizable: true,
    },
    {
      key: "errorMessage",
      name: "Error",
      minWidth: 120,
      maxWidth: 260,
      isResizable: true,
      onRender: (item: ExpressCompanyNameAuditDto) =>
        item.errorMessage ? (
          <TooltipHost content={item.errorMessage}>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
                maxWidth: "100%",
              }}
            >
              {item.errorMessage}
            </span>
          </TooltipHost>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: "16px" } }}>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
        <DefaultButton
          iconProps={{ iconName: "Back" }}
          text="Back"
          onClick={() => navigate("/rename-company")}
        />
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          Rename Company Audits
        </Text>
        <div style={{ flex: 1 }} />
        <IconButton
          iconProps={{ iconName: "Refresh" }}
          title="Refresh"
          onClick={fetchAudits}
          disabled={loading}
        />
      </Stack>

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setErrorMessage("")}
        >
          {errorMessage}
        </MessageBar>
      )}

      {loading ? (
        <ShimmerList numberOfColumns={10} />
      ) : (
        <DataGrid items={audits} columns={columns} />
      )}
    </Stack>
  );
};

export default RenameCompanyAuditsPage;
